// #65, the two questions people ask a restaurants page first: is it open
// now, and can I get a table tonight? They lead the collection chips, on the
// hubs whose venues carry opening hours. Both are URL params (open=1,
// tonight=1) filtered on the server, which judges them on Morocco's clock.
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { BEVEL_CHIP_ON, BEVEL_INSET_WHITE, BEVEL_NAVY_FACE, BEVEL_WHITE_FACE, CONTROL_EDGE } from "@majourneys/shared/ui";
import { patchSearch, readHubWhen } from "../lib/hub-when";

const NAVY = "#110E39";
const GOLD = "#E5B265";
const GREEN = "#1FA855";

export function HubTonightChips({ search }: { category: string; search: Record<string, unknown> }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const when = readHubWhen(search);
  // Each chip toggles its own param and keeps every other choice (city, tag,
  // day, time, guests, sort); page 1 again, since the list is a new one.
  const toggle = (key: "open" | "tonight", on: boolean) =>
    navigate({
      search: ((prev: Record<string, unknown>) => patchSearch(prev, { [key]: on ? undefined : 1 })) as never,
      resetScroll: false,
    });

  // "Tables tonight" is no longer offered (owner, 2026-10-14); a link that
  // still carries it shows as a removable chip on the results line.
  const chips: { key: "open" | "tonight"; on: boolean; label: string; lead: "dot" | "clock" }[] = [
    { key: "open", on: when.open, label: t("explore.quick.open_now"), lead: "dot" },
  ];

  return (
    <>
      {chips.map((c) => (
        <button
          key={c.key}
          type="button"
          aria-pressed={c.on}
          onClick={() => toggle(c.key, c.on)}
          data-testid={`quick-${c.key}`}
          className="mj-press inline-flex min-h-[44px] shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-4 py-1.5 text-[14px] font-semibold outline-none transition-[background,color,box-shadow] duration-200 focus-visible:ring-2 focus-visible:ring-[#E5B265] focus-visible:ring-offset-2"
          style={
            c.on
              ? { background: BEVEL_NAVY_FACE, color: "#fff", boxShadow: BEVEL_CHIP_ON }
              : { background: BEVEL_WHITE_FACE, color: NAVY, boxShadow: `${BEVEL_INSET_WHITE}, 0 0 0 1px ${CONTROL_EDGE}` }
          }
        >
          {c.lead === "dot" ? (
            <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full" style={{ background: GREEN }} />
          ) : (
            <Icon name="ri-time-line" size={15} color={c.on ? GOLD : NAVY} aria-hidden="true" />
          )}
          {c.label}
        </button>
      ))}
    </>
  );
}
